import { useEffect, useMemo, useState } from "react";
import { KeyRound, Pencil, Plus, Search, X } from "lucide-react";
import { useDeferredFilters, SearchButton } from "@/components/Shared";
import { toast } from "sonner";
import api, { errText } from "@/lib/api";
import {
  Card,
  KORWIL_LIST,
  KordaBadge,
  KorwilBadge,
  SectionTitle,
  StatusBadge,
  TableShell,
  Td,
  Th,
} from "@/components/Shared";

const KOPROV_PROVINCES = [
  "Aceh",
  "Sumatera Utara",
  "Sumatera Barat",
  "Riau",
  "Jambi",
  "Sumatera Selatan",
  "Bengkulu",
  "Lampung",
  "Kepulauan Bangka Belitung",
  "Kepulauan Riau",
  "Banten",
  "D.K.I. Jakarta",
  "Jawa Barat",
  "Jawa Tengah",
  "D.I. Yogyakarta",
  "Jawa Timur",
  "Bali",
  "Nusa Tenggara Barat",
  "Nusa Tenggara Timur",
  "Kalimantan Barat",
  "Kalimantan Tengah",
  "Kalimantan Selatan",
  "Kalimantan Timur",
  "Kalimantan Utara",
  "Sulawesi Utara",
  "Sulawesi Tengah",
  "Sulawesi Selatan",
  "Sulawesi Tenggara",
  "Gorontalo",
  "Sulawesi Barat",
  "Maluku",
  "Maluku Utara",
  "Papua",
  "Papua Barat",
  "Papua Barat Daya",
  "Papua Selatan",
  "Papua Tengah",
  "Papua Pegunungan",
];

const emptyForm = (korda = "") => ({
  email: "",
  password: "",
  name: "",
  role: "KORWIL",
  korda,
  korwil: "ARIPIN",
  province_scope: [],
  kabupaten_scope: [],
});

export default function Users() {
  const [items, setItems] = useState([]);
  const [kordaOptions, setKordaOptions] = useState([]);
  const [kabupatenOptions, setKabupatenOptions] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [busy, setBusy] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editBusy, setEditBusy] = useState(false);
  const { draft: filterDraft, applied: appliedFilters, set: setFilter, apply: applyFilters } = useDeferredFilters({
    query: "",
    roleFilter: "ALL",
    statusFilter: "ALL",
  });
  const query = filterDraft.query;
  const roleFilter = filterDraft.roleFilter;
  const statusFilter = filterDraft.statusFilter;
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const load = () => api.get("/users").then(({ data }) => setItems(data)).catch(() => {});

  useEffect(() => {
    load();
    api
      .get("/filters/options")
      .then(({ data }) => {
        const options = data.korda || [];
        setKordaOptions(options);
        setForm((f) => ({ ...f, korda: f.korda || options[0] || "" }));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (form.role !== "SUB_KORDA" || !form.korda) {
      setKabupatenOptions([]);
      return;
    }

    api
      .get("/filters/options", { params: { korda: form.korda } })
      .then(({ data }) => {
        const options = [...(data.kabupaten_kota || [])].sort((a, b) =>
          a.localeCompare(b, "id", { sensitivity: "base", numeric: true })
        );
        setKabupatenOptions(options);
        setForm((f) => ({
          ...f,
          kabupaten_scope: (f.kabupaten_scope || []).filter((k) =>
            options.includes(k)
          ),
        }));
      })
      .catch(() => setKabupatenOptions([]));
  }, [form.role, form.korda]);

  const toggleProvince = (province) => {
    setForm((f) => {
      const exists = f.province_scope.includes(province);
      return {
        ...f,
        province_scope: exists
          ? f.province_scope.filter((p) => p !== province)
          : [...f.province_scope, province],
      };
    });
  };

  const selectAllProvinces = () => {
    setForm((f) => ({ ...f, province_scope: [...KOPROV_PROVINCES] }));
  };

  const clearAllProvinces = () => {
    setForm((f) => ({ ...f, province_scope: [] }));
  };

  const toggleKabupaten = (kabupaten) => {
    setForm((f) => {
      const exists = f.kabupaten_scope.includes(kabupaten);
      return {
        ...f,
        kabupaten_scope: exists
          ? f.kabupaten_scope.filter((k) => k !== kabupaten)
          : [...f.kabupaten_scope, kabupaten],
      };
    });
  };

  const selectAllKabupaten = () => {
    setForm((f) => ({ ...f, kabupaten_scope: [...kabupatenOptions] }));
  };

  const clearAllKabupaten = () => {
    setForm((f) => ({ ...f, kabupaten_scope: [] }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/users", {
        ...form,
        korda: ["KORDA", "SUB_KORDA", "KOPROV"].includes(form.role) ? form.korda : null,
        korwil: ["KORWIL", "ADMIN_KORWIL"].includes(form.role)
          ? form.korwil
          : null,
        province_scope: form.role === "KOPROV" ? form.province_scope : [],
        kabupaten_scope: form.role === "SUB_KORDA" ? form.kabupaten_scope : [],
      });
      toast.success("User dibuat");
      setForm(emptyForm(kordaOptions[0] || ""));
      load();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };


  useEffect(() => {
    setPage(1);
  }, [query, roleFilter, statusFilter, perPage]);

  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((u) => {
      const matchesQuery = !q || [u.name, u.email, u.role, u.korda, u.korwil]
        .some((v) => String(v || "").toLowerCase().includes(q));
      const matchesRole = appliedFilters.roleFilter === "ALL" || u.role === appliedFilters.roleFilter;
      const matchesStatus =
        appliedFilters.statusFilter === "ALL" ||
        (appliedFilters.statusFilter === "ACTIVE" ? u.active : !u.active);
      return matchesQuery && matchesRole && matchesStatus;
    });
  }, [items, appliedFilters]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / perPage));
  const currentPage = Math.min(page, totalPages);
  const paginatedItems = filteredItems.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  const openEdit = (u) => {
    setEditForm({
      id: u.id,
      email: u.email || "",
      name: u.name || "",
      role: u.sub_korda ? "SUB_KORDA" : (u.role || "KORWIL"),
      korda: u.korda || kordaOptions[0] || "",
      korwil: u.korwil || KORWIL_LIST[0] || "ARIPIN",
      province_scope: [...(u.province_scope || [])],
      kabupaten_scope: [...(u.kabupaten_scope || [])],
    });
  };

  const toggleEditProvince = (province) => {
    setEditForm((f) => {
      const exists = (f.province_scope || []).includes(province);
      return { ...f, province_scope: exists
        ? f.province_scope.filter((p) => p !== province)
        : [...(f.province_scope || []), province] };
    });
  };

  const toggleEditKabupaten = (kabupaten) => {
    setEditForm((f) => {
      const exists = (f.kabupaten_scope || []).includes(kabupaten);
      return { ...f, kabupaten_scope: exists
        ? f.kabupaten_scope.filter((k) => k !== kabupaten)
        : [...(f.kabupaten_scope || []), kabupaten] };
    });
  };

  useEffect(() => {
    if (!editForm || editForm.role !== "SUB_KORDA" || !editForm.korda) return;
    api.get("/filters/options", { params: { korda: editForm.korda } })
      .then(({ data }) => {
        const options = [...(data.kabupaten_kota || [])].sort((a, b) =>
          a.localeCompare(b, "id", { sensitivity: "base", numeric: true })
        );
        setKabupatenOptions(options);
        setEditForm((f) => ({
          ...f,
          kabupaten_scope: (f.kabupaten_scope || []).filter((k) => options.includes(k)),
        }));
      })
      .catch(() => setKabupatenOptions([]));
  }, [editForm?.role, editForm?.korda]);

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editForm) return;
    setEditBusy(true);
    try {
      await api.patch("/users/" + editForm.id, {
        email: editForm.email,
        name: editForm.name,
        role: editForm.role,
        korda: ["KORDA", "SUB_KORDA", "KOPROV"].includes(editForm.role) ? editForm.korda : null,
        korwil: ["KORWIL", "ADMIN_KORWIL"].includes(editForm.role) ? editForm.korwil : null,
        province_scope: editForm.role === "KOPROV" ? editForm.province_scope : [],
        kabupaten_scope: editForm.role === "SUB_KORDA" ? editForm.kabupaten_scope : [],
        sub_korda: editForm.role === "SUB_KORDA",
      });
      toast.success("User berhasil diperbarui");
      setEditForm(null);
      load();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setEditBusy(false);
    }
  };

  const resetPassword = async (u) => {
    const confirmed = window.confirm(
      `Reset password ${u.name} ke password default Smartboard2026?`
    );

    if (!confirmed) return;

    try {
      await api.patch(`/users/${u.id}/reset-password`);
      toast.success(`Password ${u.name} berhasil di-reset`);
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const inputCls =
    "w-full rounded border border-slate-700 bg-slate-900 px-2.5 py-1.5 text-sm text-slate-200 outline-none focus:border-blue-500";

  return (
    <div data-testid="page-users">
      <SectionTitle title="User Management" subtitle="Hak akses SUPER_ADMIN / KORDA / KORWIL / ADMIN_KORWIL / KOPROV — scope dipaksa di backend" />

      <Card className="mb-6 p-4">
        <h3 className="mb-3 font-display text-base font-semibold text-white">Tambah User</h3>
        <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="user-form">
          <input data-testid="user-name" required placeholder="Nama" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
          <input data-testid="user-email" required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
          <input data-testid="user-password" required type="password" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} />
          <select data-testid="user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value, province_scope: e.target.value === "KOPROV" ? form.province_scope : [] })} className={inputCls}>
            {["SUPER_ADMIN", "KORDA", "SUB_KORDA", "KORWIL", "ADMIN_KORWIL", "KOPROV"].map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>

          {["KORDA", "SUB_KORDA", "KOPROV"].includes(form.role) ? (
            <select data-testid="user-korda" value={form.korda} onChange={(e) => setForm({ ...form, korda: e.target.value, kabupaten_scope: [] })} className={inputCls}>
              <option value="">Pilih Korda</option>
              {kordaOptions.map((k) => (
                <option key={k} value={k}>{k}</option>
              ))}
            </select>
          ) : (
            <div />
          )}

          {form.role === "SUB_KORDA" ? (
            <div className="sm:col-span-3 lg:col-span-6 rounded-lg border border-slate-700 bg-slate-950/60 p-3">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="text-sm font-semibold text-slate-100">Scope Kabupaten / Kota SUB KORDA</div>
                  <div className="text-[11px] text-slate-400">SUB KORDA hanya dapat melihat data dari Korda yang dipilih dan kabupaten/kota yang dicentang.</div>
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={selectAllKabupaten} className="rounded border border-blue-700 px-2 py-1 text-[11px] text-blue-300 hover:bg-blue-950/50">Pilih Semua</button>
                  <button type="button" onClick={clearAllKabupaten} className="rounded border border-slate-700 px-2 py-1 text-[11px] text-slate-300 hover:bg-slate-800">Kosongkan</button>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-x-8 gap-y-1.5 sm:grid-cols-2 lg:grid-cols-4">
                {Array.from({ length: 4 }, (_, columnIndex) => {
                  const columnSize = Math.ceil(kabupatenOptions.length / 4);
                  const columnItems = kabupatenOptions.slice(
                    columnIndex * columnSize,
                    (columnIndex + 1) * columnSize
                  );
                  return (
                    <div key={columnIndex} className="flex flex-col gap-1.5">
                      {columnItems.map((kabupaten) => (
                        <label key={kabupaten} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-xs text-slate-300 hover:bg-slate-800/70">
                          <input
                            type="checkbox"
                            checked={form.kabupaten_scope.includes(kabupaten)}
                            onChange={() => toggleKabupaten(kabupaten)}
                            className="h-3.5 w-3.5 accent-blue-600"
                          />
                          <span>{kabupaten}</span>
                        </label>
                      ))}
                    </div>
                  );
                })}
              </div>
              <div className="mt-2 text-[11px] text-slate-400">
                Dipilih: <span className="font-semibold text-blue-300">{form.kabupaten_scope.length}</span> Kabupaten / Kota
              </div>
            </div>
          ) : (
            <div />
          )}
          {["KORWIL", "ADMIN_KORWIL"].includes(form.role) ? (
            <select data-testid="user-korwil" value={form.korwil} onChange={(e) => setForm({ ...form, korwil: e.target.value })} className={inputCls}>
              {KORWIL_LIST.map((k) => (
                <option key={k} value={k}>{k}</option>
              ))}
            </select>
          ) : (
            <div />
          )}

          {form.role === "KOPROV" && (
            <div className="sm:col-span-3 lg:col-span-6 rounded-lg border border-slate-700 bg-slate-950/60 p-3">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="text-sm font-semibold text-slate-100">Scope Provinsi KOPROV</div>
                  <div className="text-[11px] text-slate-400">KOPROV hanya dapat melihat data dari Korda yang dipilih dan provinsi yang dicentang.</div>
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={selectAllProvinces} className="rounded border border-blue-700 px-2 py-1 text-[11px] text-blue-300 hover:bg-blue-950/50">Pilih Semua</button>
                  <button type="button" onClick={clearAllProvinces} className="rounded border border-slate-700 px-2 py-1 text-[11px] text-slate-300 hover:bg-slate-800">Kosongkan</button>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 lg:grid-cols-4">
                {KOPROV_PROVINCES.map((province) => (
                  <label key={province} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-xs text-slate-300 hover:bg-slate-800/70">
                    <input
                      type="checkbox"
                      checked={form.province_scope.includes(province)}
                      onChange={() => toggleProvince(province)}
                      className="h-3.5 w-3.5 accent-blue-600"
                    />
                    <span>{province}</span>
                  </label>
                ))}
              </div>
              <div className="mt-2 text-[11px] text-slate-400">
                Dipilih: <span className="font-semibold text-blue-300">{form.province_scope.length}</span> provinsi
              </div>
            </div>
          )}

          <button
            data-testid="user-submit"
            disabled={busy || (form.role === "KOPROV" && form.province_scope.length === 0) || (form.role === "SUB_KORDA" && (!form.korda || form.kabupaten_scope.length === 0))}
            className="flex items-center justify-center gap-1.5 rounded bg-blue-600 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-50 sm:col-span-3 lg:col-span-2"
          >
            <Plus className="h-3.5 w-3.5" /> Simpan User
          </button>
        </form>
      </Card>

      <Card className="mb-3 p-3">
        <div className="grid grid-cols-1 gap-2 md:grid-cols-4">
          <div className="relative md:col-span-2">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
            <input
              value={query}
              onChange={(e) => setFilter("query")(e.target.value)}
              placeholder="Cari nama, email, role, KORDA, atau KORWIL..."
              className={inputCls + " pl-9"}
            />
          </div>
          <select value={roleFilter} onChange={(e) => setFilter("roleFilter")(e.target.value)} className={inputCls}>
            <option value="ALL">Semua Role</option>
            {["SUPER_ADMIN", "KORDA", "KORWIL", "ADMIN_KORWIL", "KOPROV"].map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
          <select value={statusFilter} onChange={(e) => setFilter("statusFilter")(e.target.value)} className={inputCls}>
            <option value="ALL">Semua Status</option>
            <option value="ACTIVE">Aktif</option>
            <option value="INACTIVE">Nonaktif</option>
          </select>
          <SearchButton onClick={applyFilters} testId="users-search-button" />
        </div>
        <div className="mt-2 text-[11px] text-slate-400">
          Menampilkan <span className="font-semibold text-slate-200">{filteredItems.length}</span> user
        </div>
      </Card>

      <TableShell testId="users-table" empty={paginatedItems.length === 0}>
        <thead>
          <tr>
            <Th>Nama</Th>
            <Th>Email</Th>
            <Th>Role</Th>
            <Th>Korda</Th>
            <Th>Korwil</Th>
            <Th>Scope Provinsi</Th>
            <Th>Status</Th>
            <Th className="text-right">Aksi</Th>
          </tr>
        </thead>
        <tbody>
          {paginatedItems.map((u) => (
            <tr key={u.id} data-testid={`user-row-${u.email}`} className="border-b border-slate-800/70 hover:bg-slate-800/50">
              <Td className="text-slate-100">{u.name}</Td>
              <Td className="font-mono-data text-xs">{u.email}</Td>
              <Td><StatusBadge value={u.role} /></Td>
              <Td>{u.korda ? <KordaBadge value={u.korda} /> : <span className="text-xs text-slate-500">Semua</span>}</Td>
              <Td>{u.korwil ? <KorwilBadge value={u.korwil} /> : <span className="text-xs text-slate-500">Semua</span>}</Td>
              <Td>
                {u.role === "KOPROV" ? (
                  <div className="max-w-[320px] text-xs text-slate-300">
                    {(u.province_scope || []).length
                      ? (u.province_scope || []).join(", ")
                      : "Belum diset"}
                  </div>
                ) : (
                  <span className="text-xs text-slate-500">—</span>
                )}
              </Td>
              <Td><StatusBadge value={u.active ? "AKTIF" : "BATAL"} /></Td>
              <Td className="text-right whitespace-nowrap">
                <button
                  data-testid={"edit-user-" + u.email}
                  onClick={() => openEdit(u)}
                  className="mr-2 inline-flex items-center gap-1 rounded border border-blue-700 px-2 py-1 text-[11px] text-blue-300 transition-colors hover:bg-blue-950/50"
                >
                  <Pencil className="h-3 w-3" />
                  Edit
                </button>

                {u.role !== "SUPER_ADMIN" && (
                  <button
                    data-testid={`reset-password-${u.email}`}
                    onClick={() => resetPassword(u)}
                    className="mr-2 inline-flex items-center gap-1 rounded border border-amber-800 px-2 py-1 text-[11px] text-amber-300 transition-colors hover:bg-amber-950/50 hover:text-amber-200"
                  >
                    <KeyRound className="h-3 w-3" />
                    Reset Password
                  </button>
                )}

                <button
                  data-testid={`toggle-user-${u.email}`}
                  onClick={async () => {
                    try {
                      await api.patch(`/users/${u.id}/toggle`);
                      load();
                    } catch (e) {
                      toast.error(errText(e));
                    }
                  }}
                  className="rounded border border-slate-700 px-2 py-1 text-[11px] text-slate-300 transition-colors hover:bg-slate-800 hover:text-white"
                >
                  {u.active ? "Nonaktifkan" : "Aktifkan"}
                </button>
              </Td>
            </tr>
          ))}
        </tbody>
      </TableShell>

      <div className="mb-2 flex flex-wrap items-center justify-between gap-2 rounded border border-slate-700 bg-slate-900/40 px-3 py-2">
        <div className="text-xs text-slate-400">
          Halaman <span className="font-semibold text-slate-200">{currentPage}</span> dari <span className="font-semibold text-slate-200">{totalPages}</span>
          {" · "}{filteredItems.length} user
        </div>
        <div className="flex items-center gap-1">
          <button disabled={currentPage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="rounded border border-slate-700 px-2.5 py-1 text-xs text-slate-300 disabled:opacity-40">‹ Sebelumnya</button>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .slice(Math.max(0, currentPage - 3), Math.min(totalPages, currentPage + 2))
            .map((p) => (
              <button key={p} onClick={() => setPage(p)}
                className={p === currentPage
                  ? "rounded bg-blue-600 px-2.5 py-1 text-xs font-semibold text-white"
                  : "rounded border border-slate-700 px-2.5 py-1 text-xs text-slate-300"}>{p}</button>
            ))}
          <button disabled={currentPage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="rounded border border-slate-700 px-2.5 py-1 text-xs text-slate-300 disabled:opacity-40">Berikutnya ›</button>
        </div>
      </div>

      <div className="mb-6 flex items-center justify-between rounded border border-slate-700 bg-slate-900/40 px-3 py-2">
        <div className="text-xs text-slate-400">
          Jumlah data per halaman
        </div>
        <select
          value={perPage}
          onChange={(e) => setPerPage(Number(e.target.value))}
          className="rounded border border-slate-700 bg-slate-900 px-3 py-1.5 text-xs text-slate-200 outline-none focus:border-blue-500"
        >
          {[10, 20, 50].map((n) => (
            <option key={n} value={n}>{n} data</option>
          ))}
        </select>
      </div>


      {editForm && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4">
          <div className="max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-xl border border-slate-700 bg-slate-900 shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-700 bg-slate-900 px-5 py-4">
              <div>
                <h3 className="font-display text-base font-semibold text-white">Edit User</h3>
                <p className="text-[11px] text-slate-400">Perbarui identitas, role, dan scope akses user.</p>
              </div>
              <button type="button" onClick={() => setEditForm(null)} className="text-slate-400 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={saveEdit} className="space-y-4 p-5">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <input required placeholder="Nama" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className={inputCls} />
                <input required type="email" placeholder="Email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className={inputCls} />
                <select value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value, province_scope: e.target.value === "KOPROV" ? editForm.province_scope : [], kabupaten_scope: e.target.value === "SUB_KORDA" ? editForm.kabupaten_scope : [] })} className={inputCls}>
                  {["SUPER_ADMIN", "KORDA", "SUB_KORDA", "KORWIL", "ADMIN_KORWIL", "KOPROV"].map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>

                {["KORDA", "SUB_KORDA", "KOPROV"].includes(editForm.role) ? (
                  <select value={editForm.korda} onChange={(e) => setEditForm({ ...editForm, korda: e.target.value, kabupaten_scope: [] })} className={inputCls}>
                    <option value="">Pilih Korda</option>
                    {kordaOptions.map((k) => <option key={k} value={k}>{k}</option>)}
                  </select>
                ) : <div />}

                {["KORWIL", "ADMIN_KORWIL"].includes(editForm.role) ? (
                  <select value={editForm.korwil} onChange={(e) => setEditForm({ ...editForm, korwil: e.target.value })} className={inputCls}>
                    {KORWIL_LIST.map((k) => <option key={k} value={k}>{k}</option>)}
                  </select>
                ) : <div />}
              </div>

              {editForm.role === "KOPROV" && (
                <div className="rounded-lg border border-slate-700 bg-slate-950/60 p-3">
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="text-sm font-semibold text-slate-100">Scope Provinsi KOPROV</div>
                      <div className="text-[11px] text-slate-400">Pilih provinsi yang boleh dilihat user.</div>
                    </div>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => setEditForm({ ...editForm, province_scope: [...KOPROV_PROVINCES] })} className="rounded border border-blue-700 px-2 py-1 text-[11px] text-blue-300">Pilih Semua</button>
                      <button type="button" onClick={() => setEditForm({ ...editForm, province_scope: [] })} className="rounded border border-slate-700 px-2 py-1 text-[11px] text-slate-300">Kosongkan</button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 lg:grid-cols-4">
                    {KOPROV_PROVINCES.map((province) => (
                      <label key={province} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-xs text-slate-300 hover:bg-slate-800/70">
                        <input type="checkbox" checked={(editForm.province_scope || []).includes(province)} onChange={() => toggleEditProvince(province)} className="h-3.5 w-3.5 accent-blue-600" />
                        <span>{province}</span>
                      </label>
                    ))}
                  </div>
                  <div className="mt-2 text-[11px] text-slate-400">Dipilih: <span className="font-semibold text-blue-300">{editForm.province_scope.length}</span> provinsi</div>
                </div>
              )}

              {editForm.role === "SUB_KORDA" && (
                <div className="rounded-lg border border-slate-700 bg-slate-950/60 p-3">
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="text-sm font-semibold text-slate-100">Scope Kabupaten / Kota SUB KORDA</div>
                      <div className="text-[11px] text-slate-400">Pilih kabupaten/kota yang boleh dilihat user.</div>
                    </div>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => setEditForm({ ...editForm, kabupaten_scope: [...kabupatenOptions] })} className="rounded border border-blue-700 px-2 py-1 text-[11px] text-blue-300">Pilih Semua</button>
                      <button type="button" onClick={() => setEditForm({ ...editForm, kabupaten_scope: [] })} className="rounded border border-slate-700 px-2 py-1 text-[11px] text-slate-300">Kosongkan</button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 lg:grid-cols-4">
                    {kabupatenOptions.map((kabupaten) => (
                      <label key={kabupaten} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-xs text-slate-300 hover:bg-slate-800/70">
                        <input type="checkbox" checked={(editForm.kabupaten_scope || []).includes(kabupaten)} onChange={() => toggleEditKabupaten(kabupaten)} className="h-3.5 w-3.5 accent-blue-600" />
                        <span>{kabupaten}</span>
                      </label>
                    ))}
                  </div>
                  <div className="mt-2 text-[11px] text-slate-400">Dipilih: <span className="font-semibold text-blue-300">{editForm.kabupaten_scope.length}</span> Kabupaten / Kota</div>
                </div>
              )}

              <div className="flex justify-end gap-2 border-t border-slate-800 pt-4">
                <button type="button" onClick={() => setEditForm(null)} className="rounded border border-slate-700 px-4 py-2 text-xs text-slate-300">Batal</button>
                <button disabled={editBusy || (editForm.role === "KOPROV" && editForm.province_scope.length === 0) || (editForm.role === "SUB_KORDA" && editForm.kabupaten_scope.length === 0)} className="rounded bg-blue-600 px-4 py-2 text-xs font-semibold text-white disabled:opacity-50">
                  {editBusy ? "Menyimpan..." : "Simpan Perubahan"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
